import InternalChatAPI from 'dashboard/api/internalChat';

const state = {
  rooms: [],
  messagesByRoom: {},
  callsByRoom: {},
  activeRoomId: null,
  typingByRoom: {},
  pinnedMessagesByRoom: {},
  uiFlags: {
    fetchingRooms: false,
    fetchingMessages: {},
    sendingMessage: {},
    startingCall: {},
    endingCall: {},
    fetchingCallToken: {},
    fetchingPinned: {},
    searchingConversation: {},
    updatingMembership: {},
    fetchingAudit: {},
  },
};
const normalizeId = id => Number(id);
const normalizeMessage = message => {
  if (!message) return message;
  const senderId =
    message.sender_id ||
    message.senderId ||
    (message.sender && message.sender.id);
  return {
    ...message,
    sender_id: senderId ? Number(senderId) : senderId,
  };
};
const normalizeCall = call => {
  if (!call) return null;
  const roomIdRaw = call.room_id ?? call.roomId;
  const roomId = roomIdRaw !== undefined ? normalizeId(roomIdRaw) : roomIdRaw;
  return {
    ...call,
    room_id: roomId,
    started_by_id: call.started_by_id ? Number(call.started_by_id) : call.started_by_id,
    status: call.status || call.state || 'active',
  };
};

const isRoomMuted = room => {
  const mutedUntil = room?.user_membership?.muted_until;
  if (!mutedUntil) return false;
  return new Date(mutedUntil) > new Date();
};

const extractCallFromMessage = message => {
  const callAttr = message?.content_attributes?.call;
  if (!callAttr) return null;
  return normalizeCall({
    ...callAttr,
    room_id: callAttr.room_id || message.room_id,
  });
};

const hasMentionForUser = (message, userId) => {
  if (!message || !userId) return false;
  const content = message.content || '';
  const regex = new RegExp(`mention://user/${userId}/`, 'i');
  return regex.test(content);
};

const applyCallFromMessage = ($state, roomId, message) => {
  const call = extractCallFromMessage(message);
  if (!call) return;
  $state.callsByRoom = {
    ...$state.callsByRoom,
    [roomId]: call,
  };
};

export const getters = {
  rooms: $state => $state.rooms,
  activeRoomId: $state => $state.activeRoomId,
  messagesByRoom: $state => $state.messagesByRoom,
  callsByRoom: $state => $state.callsByRoom,
  getMessagesByRoom: $state => roomId => $state.messagesByRoom[roomId] || [],
  getRoomById: $state => roomId =>
    $state.rooms.find(room => room.id === Number(roomId)),
  getCallByRoom: $state => roomId =>
    $state.callsByRoom[normalizeId(roomId)],
  typingByRoom: $state => $state.typingByRoom,
  pinnedMessagesByRoom: $state => $state.pinnedMessagesByRoom,
  uiFlags: $state => $state.uiFlags,
};

export const actions = {
  async fetchRooms({ commit }) {
    commit('SET_FETCHING_ROOMS', true);
    try {
      const { data } = await InternalChatAPI.getRooms();
      commit('SET_ROOMS', data);
    } finally {
      commit('SET_FETCHING_ROOMS', false);
    }
  },

  async fetchMessages({ commit, state }, { roomId, limit = 50, beforeId = null }) {
    const normalizedRoomId = normalizeId(roomId);
    commit('SET_FETCHING_MESSAGES', { roomId, value: true });
    try {
      const params = { limit };
      if (beforeId) params.before_id = beforeId;
      const { data } = await InternalChatAPI.getMessages(normalizedRoomId, params);
      if (beforeId && (state.messagesByRoom[normalizedRoomId] || []).length) {
        commit('PREPEND_MESSAGES', { roomId: normalizedRoomId, messages: data });
      } else {
        commit('SET_MESSAGES', { roomId: normalizedRoomId, messages: data });
      }
    } finally {
      commit('SET_FETCHING_MESSAGES', { roomId, value: false });
    }
  },

  async sendMessage(
    { commit, rootGetters },
    { roomId, content, attachments = [], message_type: messageType, content_attributes: contentAttributes }
  ) {
    if (!content && (!attachments || attachments.length === 0)) return;
    const normalizedRoomId = normalizeId(roomId);
    commit('SET_SENDING_MESSAGE', { roomId, value: true });
    try {
      const formData = new FormData();
      formData.append('message[content]', content || '');
      formData.append('message[message_type]', messageType || (attachments.length ? 'file' : 'text'));
      if (contentAttributes) {
        Object.entries(contentAttributes).forEach(([key, value]) => {
          formData.append(`message[content_attributes][${key}]`, value);
        });
      }
      attachments.forEach(file => {
        formData.append('message[attachments][]', file);
      });

      const { data } = await InternalChatAPI.createMessage(normalizedRoomId, formData);
      commit('ADD_MESSAGE', { roomId: normalizedRoomId, message: data });
      commit('RESET_UNREAD', { roomId: normalizedRoomId });
    } finally {
      commit('SET_SENDING_MESSAGE', { roomId, value: false });
    }
    // optimistic mark read for sender
    const currentUserId = rootGetters.getCurrentUserID;
    if (currentUserId) {
      commit('MARK_READ_METADATA', { roomId, userId: currentUserId });
    }
  },

  async sendTypingStatus(_, { roomId, status }) {
    if (!roomId) return;
    try {
      await InternalChatAPI.sendTyping(normalizeId(roomId), status);
    } catch (_) {
      // ignore
    }
  },

  async updateMembership({ commit, rootGetters }, { roomId, state: stateName, mutedUntil }) {
    const normalizedRoomId = normalizeId(roomId);
    const userId = rootGetters.getCurrentUserID;
    if (!userId) return;
    commit('SET_UPDATING_MEMBERSHIP', { roomId: normalizedRoomId, value: true });
    try {
      const { data } = await InternalChatAPI.updateParticipantState(
        normalizedRoomId,
        userId,
        stateName,
        { muted_until: mutedUntil }
      );
      commit('SET_ROOM_MEMBERSHIP', { roomId: normalizedRoomId, membership: data });
      return data;
    } finally {
      commit('SET_UPDATING_MEMBERSHIP', { roomId: normalizedRoomId, value: false });
    }
  },

  async markRead({ commit, rootGetters }, { roomId }) {
    const normalizedRoomId = normalizeId(roomId);
    const currentUserId = rootGetters.getCurrentUserID;
    if (!currentUserId) return;
    commit('RESET_UNREAD', { roomId: normalizedRoomId });
    commit('SET_MENTION_BADGE', { roomId: normalizedRoomId, value: false });
    commit('MARK_READ_METADATA', {
      roomId: normalizedRoomId,
      userId: currentUserId,
    });
    try {
      await InternalChatAPI.markRead(normalizedRoomId, currentUserId);
    } catch (_) {
      // ignore failures to avoid blocking UI
    }
  },

  setActiveRoom({ commit }, roomId) {
    commit('SET_ACTIVE_ROOM', normalizeId(roomId));
  },

  async createRoom({ commit, dispatch }, { kind, name, dmUserId, memberIds }) {
    const { data } = await InternalChatAPI.createRoom({
      kind,
      name,
      dmUserId,
      memberIds,
    });
    commit('UPSERT_ROOM', data);
    commit('SET_ACTIVE_ROOM', data.id);
    commit('SET_MESSAGES', { roomId: data.id, messages: [] });
    await dispatch('markRead', { roomId: data.id });
    return data;
  },

  async addMembers({ commit }, { roomId, memberIds }) {
    const normalizedRoomId = normalizeId(roomId);
    const { data } = await InternalChatAPI.addMembers(normalizedRoomId, memberIds);
    commit('UPSERT_ROOM', data);
    return data;
  },

  async updateRoom({ commit }, { roomId, name, memberIds, metadata }) {
    const normalizedRoomId = normalizeId(roomId);
    const { data } = await InternalChatAPI.updateRoom(normalizedRoomId, {
      name,
      memberIds,
      metadata,
    });
    commit('UPSERT_ROOM', data);
    return data;
  },

  async startCall({ commit }, { roomId, internal = false }) {
    const normalizedRoomId = normalizeId(roomId);
    commit('SET_CALL_FLAG', { roomId: normalizedRoomId, flag: 'startingCall', value: true });
    try {
      const payload = internal ? { internal: true } : {};
      const { data } = await InternalChatAPI.startCall(normalizedRoomId, payload);
      commit('SET_CALL', { roomId: normalizedRoomId, call: data.call });
      return data;
    } finally {
      commit('SET_CALL_FLAG', { roomId: normalizedRoomId, flag: 'startingCall', value: false });
    }
  },

  async fetchCallToken({ commit }, { roomId, internal = false }) {
    const normalizedRoomId = normalizeId(roomId);
    commit('SET_CALL_FLAG', { roomId: normalizedRoomId, flag: 'fetchingCallToken', value: true });
    try {
      const params = internal ? { internal: true } : {};
      const { data } = await InternalChatAPI.fetchCallToken(normalizedRoomId, params);
      commit('SET_CALL', { roomId: normalizedRoomId, call: data.call });
      return data;
    } finally {
      commit('SET_CALL_FLAG', { roomId: normalizedRoomId, flag: 'fetchingCallToken', value: false });
    }
  },

  async endCall({ commit }, { roomId }) {
    const normalizedRoomId = normalizeId(roomId);
    commit('SET_CALL_FLAG', { roomId: normalizedRoomId, flag: 'endingCall', value: true });
    try {
      const { data } = await InternalChatAPI.endCall(normalizedRoomId);
      commit('SET_CALL', { roomId: normalizedRoomId, call: data.call });
      return data;
    } finally {
      commit('SET_CALL_FLAG', { roomId: normalizedRoomId, flag: 'endingCall', value: false });
    }
  },

  handleIncomingMessage({ commit, state, rootGetters }, payload) {
    const { room, message } = payload;
    if (!room || !message) return;
    const normalizedMessage = normalizeMessage(message);
    commit('UPSERT_ROOM', room);
    commit('ADD_MESSAGE', { roomId: room.id, message: normalizedMessage });
    const isActiveRoom = state.activeRoomId === room.id;
    const isMine =
      Number(normalizedMessage.sender_id) === Number(rootGetters.getCurrentUserID);
    const roomEntry = state.rooms.find(r => r.id === room.id) || room;
    const muted = isRoomMuted(roomEntry);
    const mentionedMe = hasMentionForUser(
      normalizedMessage,
      rootGetters.getCurrentUserID
    );
    if (isActiveRoom || isMine || muted) {
      commit('RESET_UNREAD', { roomId: room.id });
    } else {
      commit('INCREMENT_UNREAD', { roomId: room.id });
    }
    if (!isMine && mentionedMe) {
      commit('SET_MENTION_BADGE', { roomId: room.id, value: true });
    }
  },

  handleTypingOn({ commit, rootGetters }, payload) {
    const roomId = normalizeId(payload.room_id);
    const user = payload.user;
    if (!roomId || !user) return;
    if (Number(user.id) === Number(rootGetters.getCurrentUserID)) return;
    commit('ADD_TYPING_USER', { roomId, user });
  },

  handleTypingOff({ commit, rootGetters }, payload) {
    const roomId = normalizeId(payload.room_id);
    const user = payload.user;
    if (!roomId || !user) return;
    if (Number(user.id) === Number(rootGetters.getCurrentUserID)) return;
    commit('REMOVE_TYPING_USER', { roomId, userId: user.id });
  },

  async fetchPinnedMessages({ commit }, roomId) {
    const normalizedRoomId = normalizeId(roomId);
    commit('SET_FETCHING_PINNED', { roomId: normalizedRoomId, value: true });
    try {
      const { data } = await InternalChatAPI.getPinnedMessages(normalizedRoomId);
      commit('SET_PINNED_MESSAGES', { roomId: normalizedRoomId, pins: data });
      return data;
    } finally {
      commit('SET_FETCHING_PINNED', { roomId: normalizedRoomId, value: false });
    }
  },

  async pinMessage({ dispatch }, { roomId, messageId }) {
    await InternalChatAPI.pinMessage(normalizeId(roomId), messageId);
    return dispatch('fetchPinnedMessages', roomId);
  },

  async unpinMessage({ dispatch }, { roomId, messageId }) {
    await InternalChatAPI.unpinMessage(normalizeId(roomId), messageId);
    return dispatch('fetchPinnedMessages', roomId);
  },

  async searchInConversation({ commit }, { roomId, params }) {
    const normalizedRoomId = normalizeId(roomId);
    commit('SET_SEARCHING_CONVERSATION', { roomId: normalizedRoomId, value: true });
    try {
      const { data } = await InternalChatAPI.searchMessages(normalizedRoomId, params);
      return data;
    } finally {
      commit('SET_SEARCHING_CONVERSATION', { roomId: normalizedRoomId, value: false });
    }
  },

  async fetchAuditLogs({ commit }, { roomId, params }) {
    const normalizedRoomId = normalizeId(roomId);
    commit('SET_FETCHING_AUDIT', { roomId: normalizedRoomId, value: true });
    try {
      const { data } = await InternalChatAPI.getAuditLogs(normalizedRoomId, params);
      return data;
    } finally {
      commit('SET_FETCHING_AUDIT', { roomId: normalizedRoomId, value: false });
    }
  },

  handleRoomRead({ commit, rootGetters }, payload) {
    const { room_id: roomId, user_id: userId, last_read_at: lastReadAt } =
      payload;
    if (!roomId || !userId) return;
    commit('MARK_READ_METADATA', { roomId, userId, lastReadAt });
    if (userId === rootGetters.getCurrentUserID) {
      commit('RESET_UNREAD', { roomId });
      commit('SET_MENTION_BADGE', { roomId, value: false });
    }
  },

  handleMessageUpdate({ commit }, payload) {
    const { room_id: roomId, message } = payload;
    if (!roomId || !message) return;
    const normalizedMessage = normalizeMessage(message);
    commit('UPDATE_MESSAGE', { roomId, message: normalizedMessage });
  },

  async deleteMessage({ commit }, { roomId, messageId }) {
    const normalizedRoomId = normalizeId(roomId);
    const { data } = await InternalChatAPI.deleteMessage(normalizedRoomId, messageId);
    commit('UPDATE_MESSAGE', { roomId: normalizedRoomId, message: data });
    return data;
  },

  async addReaction({ commit }, { roomId, messageId, emoji }) {
    const normalizedRoomId = normalizeId(roomId);
    const { data } = await InternalChatAPI.addReaction(normalizedRoomId, messageId, emoji);
    commit('UPDATE_MESSAGE', { roomId: normalizedRoomId, message: data });
    return data;
  },

  async removeReaction({ commit }, { roomId, messageId, emoji }) {
    const normalizedRoomId = normalizeId(roomId);
    const { data } = await InternalChatAPI.removeReaction(normalizedRoomId, messageId, emoji);
    commit('UPDATE_MESSAGE', { roomId: normalizedRoomId, message: data });
    return data;
  },
};

export const mutations = {
  SET_ROOMS($state, rooms) {
    const callEntries = {};
    rooms.forEach(room => {
      room.has_unread_mention = room.has_unread_mention || false;
      if (room.active_call) {
        const normalizedCall = normalizeCall({
          ...room.active_call,
          room_id: room.id,
        });
        if (normalizedCall) {
          callEntries[room.id] = normalizedCall;
        }
      }
    });
    if (Object.keys(callEntries).length) {
      $state.callsByRoom = {
        ...$state.callsByRoom,
        ...callEntries,
      };
    }
    $state.rooms = rooms.sort((a, b) => {
      const pinnedA = a.user_membership?.pinned_at ? 1 : 0;
      const pinnedB = b.user_membership?.pinned_at ? 1 : 0;
      if (pinnedA !== pinnedB) return pinnedB - pinnedA;
      return (
        new Date(b.last_message_at || b.updated_at || 0) -
        new Date(a.last_message_at || a.updated_at || 0)
      );
    });
  },
  UPSERT_ROOM($state, room) {
    const normalizedRoom = {
      has_unread_mention: room.has_unread_mention || false,
      ...room,
    };
    if (room.active_call) {
      const normalizedCall = normalizeCall({
        ...room.active_call,
        room_id: room.id,
      });
      if (normalizedCall) {
        $state.callsByRoom = {
          ...$state.callsByRoom,
          [room.id]: normalizedCall,
        };
      }
    }
    const idx = $state.rooms.findIndex(r => r.id === room.id);
    if (idx >= 0) {
      $state.rooms.splice(idx, 1, { ...$state.rooms[idx], ...normalizedRoom });
    } else {
      $state.rooms.unshift(normalizedRoom);
    }
    $state.rooms.sort((a, b) => {
      const pinnedA = a.user_membership?.pinned_at ? 1 : 0;
      const pinnedB = b.user_membership?.pinned_at ? 1 : 0;
      if (pinnedA !== pinnedB) return pinnedB - pinnedA;
      return (
        new Date(b.last_message_at || b.updated_at || 0) -
        new Date(a.last_message_at || a.updated_at || 0)
      );
    });
  },
  SET_MESSAGES($state, { roomId, messages }) {
    const normalizedMessages = messages.map(normalizeMessage);
    $state.messagesByRoom = {
      ...$state.messagesByRoom,
      [roomId]: normalizedMessages,
    };
    const callMessage = [...normalizedMessages].reverse().find(msg =>
      extractCallFromMessage(msg)
    );
    if (callMessage) {
      applyCallFromMessage($state, roomId, callMessage);
    }
  },
  PREPEND_MESSAGES($state, { roomId, messages }) {
    const normalizedMessages = messages.map(normalizeMessage);
    const existing = $state.messagesByRoom[roomId] || [];
    const merged = [...normalizedMessages, ...existing];
    const deduped = [];
    const seen = new Set();
    merged.forEach(msg => {
      if (seen.has(msg.id)) return;
      seen.add(msg.id);
      deduped.push(msg);
    });
    $state.messagesByRoom = {
      ...$state.messagesByRoom,
      [roomId]: deduped,
    };
  },
  ADD_MESSAGE($state, { roomId, message }) {
    const normalizedMessage = normalizeMessage(message);
    const messages = $state.messagesByRoom[roomId] || [];
    const existingIndex = messages.findIndex(msg => msg.id === normalizedMessage.id);
    if (existingIndex >= 0) {
      messages.splice(existingIndex, 1, normalizedMessage);
    } else {
      messages.push(normalizedMessage);
    }
    $state.messagesByRoom = {
      ...$state.messagesByRoom,
      [roomId]: messages,
    };
    applyCallFromMessage($state, roomId, normalizedMessage);
    // bump room ordering
    const roomIdx = $state.rooms.findIndex(r => r.id === roomId);
    if (roomIdx >= 0) {
      const updated = {
        ...$state.rooms[roomIdx],
        last_message: normalizedMessage,
        last_message_at: normalizedMessage.created_at,
        last_message_preview: normalizedMessage.content,
      };
      $state.rooms.splice(roomIdx, 1);
      $state.rooms.unshift(updated);
    }
  },
  UPDATE_MESSAGE($state, { roomId, message }) {
    const normalizedMessage = normalizeMessage(message);
    const messages = $state.messagesByRoom[roomId] || [];
    const existingIndex = messages.findIndex(msg => msg.id === normalizedMessage.id);
    if (existingIndex >= 0) {
      messages.splice(existingIndex, 1, normalizedMessage);
      $state.messagesByRoom = {
        ...$state.messagesByRoom,
        [roomId]: [...messages],
      };
      applyCallFromMessage($state, roomId, normalizedMessage);
    }
  },
  SET_FETCHING_ROOMS($state, value) {
    $state.uiFlags.fetchingRooms = value;
  },
  SET_FETCHING_MESSAGES($state, { roomId, value }) {
    $state.uiFlags.fetchingMessages = {
      ...$state.uiFlags.fetchingMessages,
      [roomId]: value,
    };
  },
  SET_SENDING_MESSAGE($state, { roomId, value }) {
    $state.uiFlags.sendingMessage = {
      ...$state.uiFlags.sendingMessage,
      [roomId]: value,
    };
  },
  SET_ACTIVE_ROOM($state, roomId) {
    $state.activeRoomId = roomId;
  },
  SET_ROOM_MEMBERSHIP($state, { roomId, membership }) {
    const room = $state.rooms.find(r => r.id === roomId);
    if (room) {
      room.user_membership = {
        pinned_at: membership.pinned_at,
        muted_until: membership.muted_until,
        archived_at: membership.archived_at,
        last_read_at: membership.last_read_at,
      };
    }
  },
  SET_MENTION_BADGE($state, { roomId, value }) {
    const room = $state.rooms.find(r => r.id === roomId);
    if (room) {
      room.has_unread_mention = value;
    }
  },
  INCREMENT_UNREAD($state, { roomId }) {
    const room = $state.rooms.find(r => r.id === roomId);
    if (room) {
      room.unread_count = (room.unread_count || 0) + 1;
    }
  },
  RESET_UNREAD($state, { roomId }) {
    const room = $state.rooms.find(r => r.id === roomId);
    if (room) {
      room.unread_count = 0;
    }
  },
  MARK_READ_METADATA($state, { roomId, userId, lastReadAt }) {
    const room = $state.rooms.find(r => r.id === roomId);
    if (!room) return;
    const timestamp = lastReadAt || new Date().toISOString();
    room.last_read_at = timestamp;
    room.unread_count = 0;
  },
  SET_CALL($state, { roomId, call }) {
    const normalizedCall = normalizeCall(call);
    if (!normalizedCall) return;
    $state.callsByRoom = {
      ...$state.callsByRoom,
      [roomId]: normalizedCall,
    };
  },
  SET_CALL_FLAG($state, { roomId, flag, value }) {
    $state.uiFlags[flag] = {
      ...$state.uiFlags[flag],
      [roomId]: value,
    };
  },
  ADD_TYPING_USER($state, { roomId, user }) {
    const current = $state.typingByRoom[roomId] || [];
    if (current.some(u => u.id === user.id)) return;
    $state.typingByRoom = {
      ...$state.typingByRoom,
      [roomId]: [...current, user],
    };
    setTimeout(() => {
      const existing = $state.typingByRoom[roomId] || [];
      $state.typingByRoom = {
        ...$state.typingByRoom,
        [roomId]: existing.filter(u => u.id !== user.id),
      };
    }, 8000);
  },
  REMOVE_TYPING_USER($state, { roomId, userId }) {
    const current = $state.typingByRoom[roomId] || [];
    $state.typingByRoom = {
      ...$state.typingByRoom,
      [roomId]: current.filter(u => Number(u.id) !== Number(userId)),
    };
  },
  SET_PINNED_MESSAGES($state, { roomId, pins }) {
    $state.pinnedMessagesByRoom = {
      ...$state.pinnedMessagesByRoom,
      [roomId]: pins,
    };
  },
  SET_FETCHING_PINNED($state, { roomId, value }) {
    $state.uiFlags.fetchingPinned = {
      ...$state.uiFlags.fetchingPinned,
      [roomId]: value,
    };
  },
  SET_SEARCHING_CONVERSATION($state, { roomId, value }) {
    $state.uiFlags.searchingConversation = {
      ...$state.uiFlags.searchingConversation,
      [roomId]: value,
    };
  },
  SET_UPDATING_MEMBERSHIP($state, { roomId, value }) {
    $state.uiFlags.updatingMembership = {
      ...$state.uiFlags.updatingMembership,
      [roomId]: value,
    };
  },
  SET_FETCHING_AUDIT($state, { roomId, value }) {
    $state.uiFlags.fetchingAudit = {
      ...$state.uiFlags.fetchingAudit,
      [roomId]: value,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
